import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, AlertTriangle, Store, ShoppingCart, UserRound, ListChecks, Package,
  GitBranch, UsersRound, Truck, Megaphone, ReceiptIndianRupee, KeyRound, CalendarDays,
  Gauge, BadgeCheck, Camera, Globe, LogOut, ChevronDown, Warehouse, Boxes, ClipboardList, Plug, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { roleLabel } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const NAV_GROUPS = [
  { section: "Control", items: [
    { to: "/", label: "Control Tower", icon: LayoutDashboard, end: true, mod: "control_tower" },
    { to: "/action-center", label: "Action Center", icon: AlertTriangle, mod: "action_center" },
  ]},
  { section: "Commerce", items: [
    { to: "/brands", label: "Brands", icon: Store, mod: "brands" },
    { to: "/orders", label: "Orders", icon: ShoppingCart, mod: "orders" },
    { to: "/products", label: "Products", icon: Package, mod: "products" },
    { to: "/customers", label: "Customers", icon: UserRound, mod: "customers" },
    { to: "/social", label: "Social", icon: Megaphone, mod: "social" },
    { to: "/billing", label: "Billing", icon: ReceiptIndianRupee, mod: "billing" },
  ]},
  { section: "Operations", items: [
    { to: "/tasks", label: "Tasks", icon: ListChecks, mod: "tasks" },
    { to: "/approvals", label: "Approvals", icon: BadgeCheck, mod: "approvals" },
    { to: "/hubs", label: "Hubs", icon: Warehouse, mod: "hubs" },
    { to: "/inventory", label: "Inventory", icon: Boxes, mod: "inventory" },
    { to: "/queue", label: "Queue", icon: ClipboardList, mod: "queue" },
    { to: "/shipping", label: "Shipping", icon: Truck, mod: "shipping" },
    { to: "/production", label: "Production", icon: Camera, mod: "production" },
    { to: "/websites", label: "Websites", icon: Globe, mod: "websites" },
  ]},
  { section: "Management", items: [
    { to: "/workflows", label: "Automation", icon: GitBranch, mod: "automation" },
    { to: "/integrations", label: "Order Sync", icon: Plug, mod: "order_sync" },
    { to: "/team", label: "Team", icon: UsersRound, mod: "team" },
    { to: "/roles", label: "Roles & Access", icon: ShieldCheck, mod: "team" },
    { to: "/workload", label: "Workload", icon: Gauge, mod: "workload" },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, mod: "calendar" },
    { to: "/vault", label: "Access Vault", icon: KeyRound, mod: "vault" },
  ]},
];

export function canAccess(user, mod) {
  if (!user) return false;
  if (["owner", "admin", "super_admin"].includes(user.role)) return true;
  const lvl = user.permissions?.[mod];
  return !!lvl && lvl !== "none";
}

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";

function ChangePasswordGate() {
  const { user, setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.must_change_password) return null;
  const submit = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Password updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true}>
      <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm" data-testid="change-password-gate">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Set a new password</DialogTitle>
          <DialogDescription className="text-zinc-500">For security, please replace your temporary password before continuing.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <input data-testid="new-password-input" className={inputCls} type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <input data-testid="confirm-password-input" className={inputCls} type="password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <button data-testid="change-password-submit" disabled={busy} onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors disabled:opacity-60">
            {busy ? "Saving…" : "Update Password"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { brands, hubs, selectedBrand, setSelectedBrand, selectedHub, setSelectedHub } = useBrand();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => canAccess(user, it.mod)) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      <ChangePasswordGate />
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#0a0a0a] flex flex-col fixed h-screen z-20">
        <div className="px-5 py-4 border-b border-white/10">
          <div className="font-display text-lg font-bold tracking-tight leading-none">DASARRO OS</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">Commerce & Fulfillment OS</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {groups.map((g) => (
            <div key={g.section} className="mb-1">
              <div className="px-5 pt-3 pb-1 text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-600">{g.section}</div>
              {g.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end}
                  data-testid={`nav-${item.label.toLowerCase().split(" ")[0]}`}
                  className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-[13px] transition-colors border-l-2 ${isActive ? "border-white bg-white/[0.06] text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.03]"}`}>
                  <item.icon size={16} /> {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-white/10 border border-white/10 flex items-center justify-center font-display font-bold text-sm">{(user?.name || "?").charAt(0).toUpperCase()}</div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 truncate">{user?.role_name || roleLabel(user?.role)}</div>
            </div>
          </div>
          <button data-testid="logout-button" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-wider border border-white/15 text-zinc-300 hover:bg-white/5 py-2 rounded-sm transition-colors">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 ml-60 min-w-0">
        <div className="h-12 border-b border-white/10 bg-[#0a0a0a] flex items-center gap-3 px-6 sticky top-0 z-30">
          <div className="relative">
            <select data-testid="global-brand-selector" value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)}
              className="appearance-none bg-[#121212] border border-white/15 text-sm text-white rounded-sm pl-3 pr-8 py-1.5 focus:outline-none focus:border-white cursor-pointer">
              <option value="All Brands">All Brands</option>
              {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          </div>
          <div className="relative" title="Fulfillment Hub filter">
            <select data-testid="global-hub-selector" value={selectedHub} onChange={(e) => setSelectedHub(e.target.value)}
              className="appearance-none bg-[#121212] border border-white/15 text-sm text-white rounded-sm pl-3 pr-8 py-1.5 focus:outline-none focus:border-white cursor-pointer">
              <option value="All Hubs">All Hubs</option>
              {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 ml-auto">Multi-Brand · Multi-Channel · Automated Ops</span>
        </div>
        <main>{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 px-8 py-5 flex items-center justify-between">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3">{actions}</div>
    </div>
  );
}
